import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import type { Movie } from "@/types";
import { usePageMeta } from "@/hooks/usePageMeta";
import HeroSlider from "./components/HeroSlider";
import CategoryQuickNav from "./components/CategoryQuickNav";
import GuidePreview from "./components/GuidePreview";

const FEATURED_IDS = [
  "about-time",
  "la-la-land",
  "your-name",
  "notting-hill",
  "little-forest",
  "our-little-sister",
  "bouquet-of-love",
  "spirited-away",
];

export default function Home() {
  usePageMeta({
    title: "适合夫妻二人看的影片精选推荐与观影指南",
    description:
      "适合夫妻二人看的影片网站为您提供多样化的情侣电影推荐，涵盖爱情、温情、喜剧等多种类型，帮助夫妻找到适合共同欣赏的优质影视作品。",
    keywords:
      "适合夫妻二人看的影片,夫妻电影推荐,情侣电影,爱情电影,温情电影,电影观影指南,情侣共赏影片",
  });

  const featured = FEATURED_IDS.map((id) =>
    movies.find((movie) => movie.id === id)
  ).filter((movie): movie is Movie => Boolean(movie));

  return (
    <SiteLayout>
      <HeroSlider />

      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-xs font-medium tracking-widest text-primary-700">
              按类型找片
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
              今天的你们，想看哪一种心情
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-foreground-600">
            爱情、喜剧、温情三条主线，帮你们快速锁定今晚的观影方向。
          </p>
        </div>
        <div className="mt-8">
          <CategoryQuickNav />
        </div>
      </section>

      <section className="w-full bg-background-100 py-12 md:py-16">
        <div className="w-full px-4 md:px-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-xs font-medium tracking-widest text-primary-700">
                精选推荐
              </span>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                最新推荐的情侣电影
              </h2>
            </div>
            <Link
              to="/category"
              className="inline-flex w-fit items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
            >
              查看全部影片
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {featured.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-xs font-medium tracking-widest text-primary-700">
              观影指南
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
              让每一次共赏都更贴心
            </h2>
          </div>
          <Link
            to="/guide"
            className="inline-flex w-fit items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
          >
            进入专题
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>
        <div className="mt-8">
          <GuidePreview limit={2} />
        </div>
      </section>

      <section className="w-full bg-background-100 py-12 md:py-16">
        <div className="w-full px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-10">
            <div>
              <span className="text-xs font-medium tracking-widest text-accent-600">
                关于本站
              </span>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                我们只做一件事：帮你们找到想一起看的电影
              </h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
                适合夫妻二人看的影片，是一个专注于情侣共赏场景的影视推荐站。
                我们从爱情、温情、喜剧等类型中精挑细选，用清晰的分类、多维筛选和真诚的短评，
                让你们不再为今晚看什么而纠结。
              </p>
              <div className="mt-6 flex flex-wrap gap-6">
                <div>
                  <p className="font-heading text-2xl font-semibold text-primary-700">
                    {movies.length}+
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">精选中外影片</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-semibold text-primary-700">
                    3 大
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">核心观影类型</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-semibold text-primary-700">
                    100%
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">人工挑选推荐</p>
                </div>
              </div>
              <Link
                to="/about"
                className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                了解更多
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
            <div className="w-full h-64 md:h-80 overflow-hidden rounded-lg bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cozy%20living%20room%20scene%20with%20a%20couple%20watching%20a%20movie%20on%20a%20sofa%20under%20soft%20lamp%20light%20with%20blanket%20and%20snacks%2C%20soft%20orange%20and%20cream%20tones%2C%20comfortable%20home%20atmosphere%2C%20editorial%20lifestyle%20photography%20style%2C%20high%20detail&width=900&height=700&seq=home-about-intro&orientation=landscape"
                alt="夫妻在家一起观看电影"
                title="适合夫妻二人看的影片 - 温馨观影时光"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}